import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Scale,
  ArrowLeft,
  CreditCard,
  Lock,
  RefreshCw,
  XCircle,
  LifeBuoy,
  ArrowRight,
  Plus,
  Minus,
} from "lucide-react";
import { LandingFooter } from "@/components/landing/CtaBanner";

const CONTACT_EMAIL = "supravat111@gmail.com";

const PLANS = [
  { id: "free", name: "Free", monthly: "₹0", yearly: "₹0", trial: "No trial · free forever" },
  { id: "pro", name: "Pro Advocate", monthly: "₹499 / month", yearly: "₹4,990 / year", trial: "30-day free trial" },
  { id: "law_firm", name: "Law Firm", monthly: "₹999 / month", yearly: "₹9,990 / year", trial: "30-day free trial" },
];

const STEPS = [
  {
    n: "1",
    title: "Choose a plan",
    body: "Pick Pro Advocate or Law Firm from Settings inside the application, or from the Pricing page. Choose monthly or yearly billing.",
  },
  {
    n: "2",
    title: "Checkout on Razorpay",
    body: "The application hands you off to Razorpay's secure checkout. Your card, UPI, netbanking or wallet details are entered into Razorpay — not into Advocate Diary.",
  },
  {
    n: "3",
    title: "Payment verified",
    body: "Razorpay confirms the payment and notifies Advocate Diary through a signature-verified webhook. Only verified notifications are accepted.",
  },
  {
    n: "4",
    title: "Subscription activated",
    body: "Once the payment is verified, your account is moved onto the plan you chose. You can see the plan, status and next renewal date in Settings.",
  },
];

const FAQ = [
  {
    q: "Which payment provider is used?",
    a: (
      <>
        Paid subscriptions are processed through <strong className="text-navy">Razorpay</strong>, a payment
        gateway. When you subscribe, you are taken to Razorpay&rsquo;s checkout to complete the payment.
        Razorpay handles the payment transaction itself.
      </>
    ),
  },
  {
    q: "Is my payment information stored by Advocate Diary?",
    a: (
      <>
        No. Advocate Diary does not store card numbers, CVV, UPI IDs, UPI PINs, net banking credentials or any
        other payment instrument credentials. Those are entered into and handled by Razorpay. Advocate Diary
        only stores subscription and payment records returned by the gateway — such as your plan, the
        subscription status, and gateway reference IDs — so that your account and receipts are correct.
      </>
    ),
  },
  {
    q: "What happens after payment?",
    a: (
      <>
        Once Razorpay confirms the payment, it notifies Advocate Diary through a signature-verified webhook.
        Unsigned or forged notifications are rejected. On a verified payment, your account is moved onto the
        plan you selected and the paid features become available. You can check the current plan, status and
        next renewal date in Settings.
      </>
    ),
  },
  {
    q: "Can I cancel?",
    a: (
      <>
        Yes. You can cancel a paid subscription at any time from Settings inside the application. Cancellation
        stops further renewals and takes effect at the end of the billing period you have already paid for.
        You continue to have access to the paid features until then. Cancellation by itself does not
        automatically issue a refund — see the next question.
      </>
    ),
  },
  {
    q: "How do I request a refund?",
    a: (
      <>
        Refund eligibility is described in our{" "}
        <Link
          to="/refund-policy"
          data-testid="payment-faq-refund-link"
          className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
        >
          Cancellation &amp; Refund Policy
        </Link>
        . To request a refund, email us at{" "}
        <a
          href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Advocate Diary — Payment / refund issue")}`}
          data-testid="payment-faq-refund-email"
          className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
        >
          {CONTACT_EMAIL}
        </a>{" "}
        from the account email address, and include the plan, approximate date and amount, and any Razorpay
        payment or order reference you have. Cancellation and refund eligibility are separate matters: not
        every cancellation results in a refund.
      </>
    ),
  },
  {
    q: "What happens if a payment fails?",
    a: (
      <>
        If Razorpay does not confirm a payment as successful, the subscription is not activated. Nothing is
        due to be refunded from Advocate Diary when the payment did not actually complete at the gateway.
        Banks sometimes show pending or authorised amounts that reverse on their own — please check with
        your bank first. If a completed charge is not reflected in Advocate Diary,{" "}
        <Link
          to="/contact"
          data-testid="payment-faq-failed-contact"
          className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
        >
          contact us
        </Link>{" "}
        with the Razorpay reference so we can check.
      </>
    ),
  },
];

const HIGHLIGHTS = [
  {
    icon: CreditCard,
    title: "Razorpay checkout",
    body: "Payments are processed by Razorpay. The application hands off to their secure checkout and returns you on completion.",
  },
  {
    icon: Lock,
    title: "No card details stored",
    body: "Card numbers, CVV, UPI PINs and netbanking credentials are entered into Razorpay — never into Advocate Diary.",
  },
  {
    icon: RefreshCw,
    title: "Verified before activation",
    body: "Subscription status changes only after Razorpay confirms the payment through a signature-verified webhook.",
  },
  {
    icon: XCircle,
    title: "Cancel from Settings",
    body: "Cancel a subscription at any time. It takes effect at the end of the billing period you have already paid for.",
  },
];

const FaqItem = ({ item, open, onToggle, idx }) => (
  <div
    data-testid={`payment-faq-item-${idx}`}
    className="overflow-hidden rounded-lg border border-border bg-white"
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-testid={`payment-faq-toggle-${idx}`}
      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-sm font-semibold text-navy transition-colors hover:bg-secondary/40"
    >
      <span>{item.q}</span>
      {open ? (
        <Minus className="h-4 w-4 shrink-0 text-gold-dark" strokeWidth={2.5} />
      ) : (
        <Plus className="h-4 w-4 shrink-0 text-gold-dark" strokeWidth={2.5} />
      )}
    </button>
    {open && (
      <div
        data-testid={`payment-faq-body-${idx}`}
        className="border-t border-border px-5 pb-5 pt-4 text-sm leading-relaxed text-muted-foreground"
      >
        {item.a}
      </div>
    )}
  </div>
);

export default function PaymentPage() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div data-testid="payment-page" className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="payment-brand" className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-navy text-gold">
              <Scale className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <span className="font-display text-xl tracking-tight text-navy">Advocate Diary</span>
          </Link>
          <Link
            to="/"
            data-testid="payment-back-home"
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy/70 transition-colors hover:bg-secondary hover:text-navy"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="border-b border-border bg-navy px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold">
            Payments &amp; Billing
          </p>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            How payment works on Advocate Diary
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
            Paid subscriptions are processed through Razorpay. This page explains what that means, what
            happens after checkout, and how cancellation and refund requests are handled.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/pricing"
              data-testid="payment-hero-pricing"
              className="inline-flex items-center gap-2 rounded-md bg-gold px-5 py-3 text-sm font-semibold text-navy shadow-lg transition-all hover:-translate-y-0.5 hover:bg-gold-light"
            >
              See pricing
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="payment-hero-contact"
              className="inline-flex items-center gap-2 rounded-md border border-white/25 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              <LifeBuoy className="h-4 w-4" />
              Contact support
            </Link>
          </div>
        </div>
      </div>

      {/* Highlights */}
      <section className="border-b border-border bg-secondary/40 px-4 py-12 sm:px-6 sm:py-14 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {HIGHLIGHTS.map((h) => {
            const Icon = h.icon;
            const slug = h.title.toLowerCase().replace(/\s+/g, "-");
            return (
              <div
                key={h.title}
                data-testid={`payment-highlight-${slug}`}
                className="rounded-xl border border-border bg-white p-5"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-navy text-gold">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <h3 className="mt-4 font-display text-base font-semibold text-navy">{h.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        {/* Plans quick summary */}
        <section data-testid="payment-plans" className="max-w-4xl">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
            Subscription plans
          </p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-navy sm:text-[1.7rem]">
            What you can subscribe to
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-[0.95rem]">
            Advocate Diary offers three plans — Free, Pro Advocate and Law Firm. Every new account starts on
            a 30-day trial of the paid features, without a card or any other payment instrument. A full
            feature comparison is on the{" "}
            <Link
              to="/pricing"
              data-testid="payment-plans-pricing"
              className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
            >
              Pricing page
            </Link>
            .
          </p>

          <div className="mt-6 overflow-hidden rounded-xl border border-border bg-white">
            <table className="w-full min-w-[560px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/40 text-xs uppercase tracking-wider text-gold-dark">
                  <th scope="col" className="px-6 py-3 font-semibold">Plan</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Monthly</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Yearly</th>
                  <th scope="col" className="px-4 py-3 font-semibold">Trial</th>
                </tr>
              </thead>
              <tbody>
                {PLANS.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`payment-plan-${p.id}`}
                    className="border-b border-border/70 last:border-b-0"
                  >
                    <th scope="row" className="px-6 py-3 font-display text-base font-semibold text-navy">
                      {p.name}
                    </th>
                    <td className="px-4 py-3 text-navy/85">{p.monthly}</td>
                    <td className="px-4 py-3 text-navy/85">{p.yearly}</td>
                    <td className="px-4 py-3 text-navy/85">{p.trial}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Prices in INR. Applicable taxes, if any, are added at checkout by the payment gateway.
          </p>
        </section>

        {/* How payment works — steps */}
        <section data-testid="payment-flow" className="mt-16 max-w-4xl">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
            Payment flow
          </p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-navy sm:text-[1.7rem]">
            What happens when you subscribe
          </h2>
          <ol className="mt-6 space-y-4">
            {STEPS.map((s) => (
              <li
                key={s.n}
                data-testid={`payment-step-${s.n}`}
                className="flex gap-4 rounded-lg border border-border bg-white p-5"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-navy text-sm font-semibold text-gold">
                  {s.n}
                </span>
                <div>
                  <p className="font-display text-base font-semibold text-navy">{s.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Cancellation vs refunds */}
        <section
          data-testid="payment-cancel-vs-refund"
          className="mt-16 grid gap-6 lg:grid-cols-2"
        >
          <div className="rounded-xl border border-border bg-white p-6 sm:p-8">
            <h3 className="font-display text-lg font-semibold text-navy">Cancellation</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              You can cancel a paid subscription from Settings inside the application at any time.
              Cancellation stops further renewals and takes effect at the end of the billing period you have
              already paid for. Access to the paid features continues until then.
            </p>
          </div>
          <div className="rounded-xl border border-border bg-white p-6 sm:p-8">
            <h3 className="font-display text-lg font-semibold text-navy">Refund eligibility</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Cancelling a subscription and being eligible for a refund are two different things.
              Refunds are not automatic; they are considered where the payment or the situation clearly
              warrants one. The full rules are in the{" "}
              <Link
                to="/refund-policy"
                data-testid="payment-cancel-refund-link"
                className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
              >
                Cancellation &amp; Refund Policy
              </Link>
              .
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" data-testid="payment-faq" className="mt-16 max-w-4xl">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
            Frequently asked
          </p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-navy sm:text-[1.7rem]">
            Payment questions
          </h2>
          <div className="mt-6 space-y-3">
            {FAQ.map((item, i) => (
              <FaqItem
                key={item.q}
                item={item}
                idx={i}
                open={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              />
            ))}
          </div>
        </section>

        {/* Support callout */}
        <section
          data-testid="payment-support-callout"
          className="mt-16 flex flex-col items-start gap-4 rounded-xl border border-border bg-secondary/40 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8"
        >
          <div>
            <h3 className="font-display text-lg font-semibold text-navy">Need help with a payment?</h3>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              For payment failures, duplicate charges, billing questions or refund requests, contact us and
              we will look into it. Please write from the email address on your Advocate Diary account.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Advocate Diary — Payment / refund issue")}`}
              data-testid="payment-support-email"
              className="inline-flex items-center gap-2 rounded-md bg-gold px-5 py-3 text-sm font-semibold text-navy shadow-sm transition-all hover:-translate-y-0.5 hover:bg-gold-light"
            >
              Email support
            </a>
            <Link
              to="/contact"
              data-testid="payment-support-contact"
              className="inline-flex items-center gap-2 rounded-md border border-navy/20 px-5 py-3 text-sm font-semibold text-navy transition-colors hover:bg-white"
            >
              Contact page
            </Link>
          </div>
        </section>

        {/* Legal + related */}
        <p className="mt-10 text-xs leading-relaxed text-muted-foreground">
          Full details are in the{" "}
          <Link
            to="/pricing"
            data-testid="payment-legal-pricing"
            className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
          >
            Pricing page
          </Link>
          , the{" "}
          <Link
            to="/refund-policy"
            data-testid="payment-legal-refund"
            className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
          >
            Cancellation &amp; Refund Policy
          </Link>
          , the{" "}
          <Link
            to="/terms"
            data-testid="payment-legal-terms"
            className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
          >
            Terms of Service
          </Link>{" "}
          and the{" "}
          <Link
            to="/privacy-policy"
            data-testid="payment-legal-privacy"
            className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
          >
            Privacy Policy
          </Link>
          . Razorpay&rsquo;s own handling of your payment details is governed by Razorpay&rsquo;s terms and
          privacy policy.
        </p>
      </main>

      <LandingFooter />
    </div>
  );
}
